// Platform automation configuration component with comprehensive settings
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTimezone } from '@/hooks/useTimezone';
import {
  Clock,
  Calendar,
  AlertTriangle,
  Cog,
  Save,
  GripVertical,
  Eye,
  Bot,
  Search,
  Upload
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Add SortableStepCard component
const SortableStepCard: React.FC<{
  id: string;
  step: string;
  enabled: boolean;
  onToggle: (step: string, enabled: boolean) => void;
  disabled?: boolean;
  type: 'image' | 'video';
}> = ({ id, step, enabled, onToggle, disabled = false, type }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'SEARCH_EXISTING':
        return <Search className="h-4 w-4" />;
      case 'AI_GENERATE':
        return <Bot className="h-4 w-4" />;
      case 'STOCK_SEARCH':
        return <Eye className="h-4 w-4" />;
      case 'STOP_AND_UPLOAD':
        return <Upload className="h-4 w-4" />;
      default:
        return <div className="h-4 w-4 rounded bg-gray-300" />;
    }
  };

  const getStepLabel = (step: string) => {
    return step.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStepDescription = (step: string, type: 'image' | 'video') => {
    const costs = {
      image: {
        SEARCH_EXISTING: 1,
        STOCK_SEARCH: 10,
        AI_GENERATE: 100,
        STOP_AND_UPLOAD: 0
      },
      video: {
        SEARCH_EXISTING: 1,
        STOCK_SEARCH: 100,
        AI_GENERATE: 900,
        STOP_AND_UPLOAD: 0
      }
    };

    switch (step) {
      case 'SEARCH_EXISTING':
        return (
          <div className="flex flex-col">
            <span>Search through existing brand assets</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      case 'AI_GENERATE':
        return (
          <div className="flex flex-col">
            <span>Generate new content using AI</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      case 'STOCK_SEARCH':
        return (
          <div className="flex flex-col">
            <span>Search stock photo/video libraries</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      case 'STOP_AND_UPLOAD':
        return (
          <div className="flex flex-col">
            <span>Stop and prompt user to upload manually</span>
            <span className="text-purple-600 font-medium">{costs[type][step]} credits</span>
          </div>
        );
      default:
        return '';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative overflow-hidden flex items-center justify-between p-4 bg-gradient-to-br from-white via-blue-50/20 to-white border border-blue-200/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] backdrop-blur-sm",
        isDragging && "shadow-2xl ring-2 ring-gradient-to-r ring-from-blue-400 ring-to-indigo-500 ring-opacity-60 scale-105 bg-gradient-to-br from-blue-50 to-indigo-50",
        !enabled && "opacity-60 grayscale",
        disabled && "cursor-not-allowed",
        enabled && "hover:from-blue-50/60 hover:to-indigo-50/40"
      )}
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Decorative gradient orb */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-8 translate-x-8" />
        
        <button
          type="button"
          className={cn(
            "relative touch-none p-2 text-gray-400 hover:text-blue-600 cursor-grab active:cursor-grabbing rounded-lg hover:bg-blue-50/50 transition-all duration-200 group-hover:scale-110",
            disabled && "cursor-not-allowed opacity-50"
          )}
          {...attributes}
          {...listeners}
          disabled={disabled}
        >
          <GripVertical className="h-5 w-5" />
        </button>
        
        <div className="relative p-2 rounded-lg bg-gradient-to-br from-blue-100/60 to-indigo-100/60 text-blue-700 group-hover:from-blue-200/60 group-hover:to-indigo-200/60 transition-all duration-300">
          {getStepIcon(step)}
        </div>
        
        <div className="relative flex-1">
          <div className="font-bold text-base bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-800 group-hover:to-indigo-700 transition-all duration-300">
            {getStepLabel(step)}
          </div>
          <div className="text-sm text-gray-600 mt-1 group-hover:text-gray-700 transition-colors duration-200">
            {getStepDescription(step, type)}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <div className="relative">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(step, e.target.checked)}
            disabled={disabled}
            className="relative w-5 h-5 rounded-md border-2 border-blue-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 hover:border-blue-400 checked:bg-gradient-to-r checked:from-blue-500 checked:to-indigo-600 checked:border-transparent checked:shadow-lg checked:shadow-blue-200/50"
          />
        </div>
      </div>
    </div>
  );
};
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';

interface PlatformAutomationConfigProps {
  platform: any;
  platformSettings: any;
  savePlatformSettings: (platformId: string, settings: any) => Promise<void>;
  isConnected: boolean;
  brandId: string;
}

export const PlatformAutomationConfig: React.FC<PlatformAutomationConfigProps> = ({
  platform,
  platformSettings,
  savePlatformSettings,
  isConnected: _isConnected,
  brandId
}) => {
  const { timezone, formatTime, isLoading } = useTimezone();
  const navigate = useNavigate();
  
  const settings = platformSettings[platform.id] || {
    autopost_enabled: false,
    autogenerate_enabled: false,
    posting_frequency: 'WEEKLY',
    preferred_time: '09:00',
    preferred_days: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
    status: 'DISCONNECTED',
    connection_details: null,
    config: {
      image: {
        enabled: true,
        steps: ["SEARCH_EXISTING", "AI_GENERATE", "STOCK_SEARCH", "STOP_AND_UPLOAD"]
      },
      video: {
        enabled: false,
        steps: ["SEARCH_EXISTING", "STOP_AND_UPLOAD", "AI_GENERATE", "STOCK_SEARCH"]
      }
    }
  };

  // Convert UTC preferred time to user's timezone for display
  const displayPreferredTime = useMemo(() => {
    if (!settings.preferred_time) return '09:00';
    
    if (isLoading) {
      return settings.preferred_time;
    }
    
    try {
      const [utcHours, utcMinutes] = settings.preferred_time.split(':').map(Number);
      const today = new Date();
      const utcDate = new Date(Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate(),
        utcHours, utcMinutes, 0, 0
      ));
      
      const localTime = utcDate.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });
      
      return localTime;
    } catch (error) {
      console.error('Error converting display time:', error);
      return '09:00';
    }
  }, [settings.preferred_time, timezone, isLoading]);

  const [formState, setFormState] = useState({
    autopost_enabled: settings.autopost_enabled || false,
    autogenerate_enabled: settings.autogenerate_enabled || false,
    posting_frequency: settings.posting_frequency || 'WEEKLY',
    preferred_time: displayPreferredTime,
    preferred_days: settings.preferred_days || ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
    config: settings.config || {
      image: {
        enabled: true,
        steps: ["SEARCH_EXISTING", "AI_GENERATE", "STOCK_SEARCH", "STOP_AND_UPLOAD"]
      },
      video: {
        enabled: false,
        steps: ["SEARCH_EXISTING", "STOP_AND_UPLOAD", "AI_GENERATE", "STOCK_SEARCH"]
      }
    }
  });

  // Sync formState.preferred_time with displayPreferredTime when it changes
  useEffect(() => {
    setFormState((prev: any) => ({
      ...prev,
      preferred_time: displayPreferredTime
    }));
  }, [displayPreferredTime]);

  // Sync formState with settings when they change
  useEffect(() => {
    const settingsPreferredDays = (settings as any).preferred_days || ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    
    setFormState((prev: any) => ({
      ...prev,
      autopost_enabled: settings.autopost_enabled || false,
      autogenerate_enabled: settings.autogenerate_enabled || false,
      posting_frequency: settings.posting_frequency || 'WEEKLY',
      preferred_days: settingsPreferredDays,
      config: settings.config || {
        image: {
          enabled: true,
          steps: ["SEARCH_EXISTING", "AI_GENERATE", "STOCK_SEARCH", "STOP_AND_UPLOAD"]
        },
        video: {
          enabled: false,
          steps: ["SEARCH_EXISTING", "STOP_AND_UPLOAD", "AI_GENERATE", "STOCK_SEARCH"]
        }
      }
    }));
  }, [settings]);

  const [saving, setSaving] = useState(false);

  // Add constants for all available steps
  const ALL_IMAGE_STEPS = ["SEARCH_EXISTING", "AI_GENERATE", "STOCK_SEARCH", "STOP_AND_UPLOAD"];
  const ALL_VIDEO_STEPS = ["SEARCH_EXISTING", "STOP_AND_UPLOAD", "AI_GENERATE", "STOCK_SEARCH"];

  // Add state to track the current order of all steps
  const [imageStepsOrder, setImageStepsOrder] = useState(ALL_IMAGE_STEPS);
  const [videoStepsOrder, setVideoStepsOrder] = useState(ALL_VIDEO_STEPS);

  // Configure drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChange = (field: string, value: any) => {
    // If enabling autopost, also enable autocreate
    if (field === 'autopost_enabled' && value === true) {
      setFormState(prev => ({
        ...prev,
        [field]: value,
        autogenerate_enabled: true
      }));
    } else if (field === 'autogenerate_enabled' && value === false) {
      // If disabling autocreate, also disable autopost and media features
      setFormState(prev => ({
        ...prev,
        [field]: value,
        autopost_enabled: false,
        config: {
          ...prev.config,
          image: {
            ...prev.config.image,
            enabled: false
          },
          video: {
            ...prev.config.video,
            enabled: false
          }
        }
      }));
    } else {
      setFormState(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDayToggle = (day: string) => {
    setFormState((prev: any) => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day) 
        ? prev.preferred_days.filter((d: string) => d !== day)
        : [...prev.preferred_days, day]
    }));
  };

  // Add drag end handler for image steps
  const handleImageDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = imageStepsOrder.indexOf(active.id as string);
    const newIndex = imageStepsOrder.indexOf(over.id as string);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(imageStepsOrder, oldIndex, newIndex);
      setImageStepsOrder(newOrder);
      
      // Update the order of enabled steps to match the new arrangement
      const enabledSteps = formState.config.image?.steps || [];
      const reorderedEnabledSteps = newOrder.filter(step => enabledSteps.includes(step));
      
      setFormState((prev: any) => ({
        ...prev,
        config: {
          ...prev.config,
          image: {
            enabled: prev.config.image?.enabled || false,
            steps: reorderedEnabledSteps
          }
        }
      }));
    }
  };

  // Add drag end handler for video steps
  const handleVideoDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = videoStepsOrder.indexOf(active.id as string);
    const newIndex = videoStepsOrder.indexOf(over.id as string);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(videoStepsOrder, oldIndex, newIndex);
      setVideoStepsOrder(newOrder);
      
      // Update the order of enabled steps to match the new arrangement
      const enabledSteps = formState.config.video?.steps || [];
      const reorderedEnabledSteps = newOrder.filter(step => enabledSteps.includes(step));
      
      setFormState((prev: any) => ({
        ...prev,
        config: {
          ...prev.config,
          video: {
            enabled: prev.config.video?.enabled || false,
            steps: reorderedEnabledSteps
          }
        }
      }));
    }
  };

  // Add toggle handlers for steps
  const handleImageStepToggle = (step: string, enabled: boolean) => {
    const currentSteps = formState.config.image?.steps || [];
    const newSteps = enabled
      ? [...currentSteps, step]
      : currentSteps.filter((s: string) => s !== step);
    
    setFormState((prev: any) => ({
      ...prev,
      config: {
        ...prev.config,
        image: {
          enabled: prev.config.image?.enabled || false,
          steps: newSteps
        }
      }
    }));
  };

  const handleVideoStepToggle = (step: string, enabled: boolean) => {
    const currentSteps = formState.config.video?.steps || [];
    const newSteps = enabled
      ? [...currentSteps, step]
      : currentSteps.filter((s: string) => s !== step);
    
    setFormState((prev: any) => ({
      ...prev,
      config: {
        ...prev.config,
        video: {
          enabled: prev.config.video?.enabled || false,
          steps: newSteps
        }
      }
    }));
  };

  const handleSave = async () => {
    
    setSaving(true);
    try {
      // Convert local timezone time to UTC for storage
      const [targetHours, targetMinutes] = formState.preferred_time.split(':').map(Number);
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const day = today.getDate();
      
      let utcTimeString = formState.preferred_time; // fallback
      
      // Find the UTC time that displays as the target local time
      searchLoop: for (let utcHour = 0; utcHour < 24; utcHour++) {
        for (let utcMinute = 0; utcMinute < 60; utcMinute += 5) {
          const utcDate = new Date(Date.UTC(year, month, day, utcHour, utcMinute, 0));
          const displayedTime = utcDate.toLocaleTimeString('en-US', {
            timeZone: timezone,
            hour12: false,
            hour: '2-digit',
            minute: '2-digit'
          });
          
          const [displayedHour, displayedMinute] = displayedTime.split(':').map(Number);
          
          if (displayedHour === targetHours && Math.abs(displayedMinute - targetMinutes) <= 5) {
            for (let fineMinute = Math.max(0, utcMinute - 5); fineMinute <= Math.min(59, utcMinute + 5); fineMinute++) {
              const fineUTCDate = new Date(Date.UTC(year, month, day, utcHour, fineMinute, 0));
              const fineDisplayedTime = fineUTCDate.toLocaleTimeString('en-US', {
                timeZone: timezone,
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
              });
              
              const [fineDisplayedHour, fineDisplayedMinute] = fineDisplayedTime.split(':').map(Number);
              
              if (fineDisplayedHour === targetHours && fineDisplayedMinute === targetMinutes) {
                utcTimeString = `${String(utcHour).padStart(2, '0')}:${String(fineMinute).padStart(2, '0')}`;
                break searchLoop;
              }
            }
            
            utcTimeString = `${String(utcHour).padStart(2, '0')}:${String(utcMinute).padStart(2, '0')}`;
            break searchLoop;
          }
        }
      }

      await savePlatformSettings(platform.id, {
        autopost_enabled: formState.autopost_enabled,
        autogenerate_enabled: formState.autogenerate_enabled,
        posting_frequency: formState.posting_frequency as "HOURLY" | "DAILY" | "WEEKLY" | "BIWEEKLY" | "MONTHLY",
        preferred_time: utcTimeString,
        preferred_days: formState.preferred_days,
        config: formState.config
      });
      
      // Redirect to connections page after successful save
      navigate(`/brands/${brandId}/connections`);
    } catch (error) {
      console.error('Error saving automation settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrentTime = () => {
    if (isLoading) return 'Loading...';
    try {
      return formatTime(new Date(), false);
    } catch (error) {
      return new Date().toLocaleTimeString();
    }
  };

  const days = [
    { value: 'MONDAY', label: 'Mon' },
    { value: 'TUESDAY', label: 'Tue' },
    { value: 'WEDNESDAY', label: 'Wed' },
    { value: 'THURSDAY', label: 'Thu' },
    { value: 'FRIDAY', label: 'Fri' },
    { value: 'SATURDAY', label: 'Sat' },
    { value: 'SUNDAY', label: 'Sun' }
  ];

  // Helper function to format selected days for display
  const formatSelectedDays = () => {
    if (formState.preferred_days.length === 0) return 'no days';
    if (formState.preferred_days.length === 7) return 'daily';

    const selectedDayLabels = formState.preferred_days
      .map((dayValue: string) => days.find(d => d.value === dayValue)?.label)
      .filter(Boolean)
      .join(', ');

    return selectedDayLabels;
  };

  // Helper function to generate dynamic behavior text
  const generateBehaviorText = () => {
    const platformName = platform.name;
    const selectedDays = formatSelectedDays();
    const hasImages = formState.config.image?.enabled;
    const hasVideos = formState.config.video?.enabled;
    const autoPublishEnabled = formState.autopost_enabled;
    const publishTime = formState.preferred_time;
    const autoGenerateEnabled = formState.autogenerate_enabled;

    const lines = [];

    // Handle disabled auto-create state
    if (!autoGenerateEnabled) {
      lines.push(`Automated content generation is disabled for ${platformName}.`);
      lines.push('Content must be created manually through the dashboard.');
      return lines;
    }

    // Line 1: Basic auto-create behavior
    lines.push(`Auto-create will create ${platformName} content ${selectedDays} and send to your inbox.`);

    // Line 2: Image/video behavior
    const mediaActions = [];
    const mediaTypes = [];

    if (hasImages || hasVideos) {
      mediaActions.push('automatically search existing');

      // Check if any image or video steps include stock search
      const hasStockSearch = (hasImages && formState.config.image?.steps?.includes('STOCK_SEARCH')) ||
                           (hasVideos && formState.config.video?.steps?.includes('STOCK_SEARCH'));
      if (hasStockSearch) {
        mediaActions.push('search stock');
      }

      // Check if any image or video steps include AI generation and build media types list
      const hasImageGenerate = hasImages && formState.config.image?.steps?.includes('AI_GENERATE');
      const hasVideoGenerate = hasVideos && formState.config.video?.steps?.includes('AI_GENERATE');

      if (hasImageGenerate && hasVideoGenerate) {
        mediaTypes.push('images and videos');
      } else if (hasImageGenerate) {
        mediaTypes.push('images');
      } else if (hasVideoGenerate) {
        mediaTypes.push('videos');
      }

      if (mediaTypes.length > 0) {
        mediaActions.push(`generate ${mediaTypes.join(' and ')}`);
      }
    }

    if (mediaActions.length > 0) {
      lines.push(`It will ${mediaActions.join(', ')}.`);
    } else {
      lines.push('It will not automatically search existing, search stock, or generate media.');
    }

    // Line 3: Auto-publish behavior
    if (autoPublishEnabled) {
      lines.push(`It will automatically publish content to ${platformName} on specified days at ${publishTime}.`);
    } else {
      lines.push(`It will not automatically publish content to ${platformName}.`);
    }

    return lines;
  };



  return (
    <div className="space-y-6">
      {/* Auto-create Behavior Alert - moved to top */}
      {formState.autogenerate_enabled && (
        <Alert className="relative overflow-hidden bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 shadow-lg backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 to-indigo-50/30" />
          <AlertTriangle className="relative h-5 w-5 text-blue-600" />
          <AlertDescription className="relative text-blue-800">
            <div className="font-bold text-blue-900 text-base mb-2">Auto-Create Content Behavior</div>
            <div className="text-sm space-y-1">
              {generateBehaviorText().map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Auto-create Toggle with Day Selection */}
      <Card className="group relative overflow-hidden border border-emerald-200/50 bg-gradient-to-br from-white via-emerald-50/20 to-white shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/20 via-transparent to-green-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-200/20 to-transparent rounded-full -translate-y-10 translate-x-10" />
        <CardContent className="relative p-6 space-y-4">
          <div className="flex flex-row items-center justify-between">
            <div className="space-y-0.5">
              <label className="text-lg font-bold bg-gradient-to-r from-emerald-800 to-green-700 bg-clip-text text-transparent">
                Auto-create Content
              </label>
              <p className="text-sm text-gray-600">
                Automatically create content for {platform.name}
              </p>
            </div>
            <Switch
              checked={formState.autogenerate_enabled}
              onCheckedChange={(checked) => handleChange('autogenerate_enabled', checked)}
              aria-label="Auto-create content"
              className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-emerald-500 data-[state=checked]:to-green-600"
            />
          </div>

          {/* Day Selection - only visible when auto-create is enabled */}
          {formState.autogenerate_enabled && (
            <div className="space-y-3 pt-4 border-t border-emerald-100">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-emerald-600" />
                <label className="text-base font-semibold text-emerald-800">
                  Posting Days
                </label>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {days.map(day => (
                  <Button
                    key={day.value}
                    variant={formState.preferred_days.includes(day.value) ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleDayToggle(day.value)}
                    className={cn(
                      "transition-all duration-300",
                      formState.preferred_days.includes(day.value)
                        ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md hover:from-emerald-600 hover:to-green-700"
                        : "hover:bg-emerald-50"
                    )}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
              <p className="text-sm text-gray-600">
                Select the days when you want content to be published
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content Creation Frequency - HIDDEN as requested */}
      {/* Frequency selection removed from UI but keeping the state management */}



      {/* Auto-Publish Content with Time Selection */}
      <Card className="group relative overflow-hidden border border-blue-200/50 bg-gradient-to-br from-white via-blue-50/20 to-white shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-transparent to-indigo-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <CardContent className="relative p-6 space-y-4">
          <div className="flex flex-row items-center justify-between">
            <div className="space-y-0.5">
              <label className="text-lg font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent">
                Auto-Publish Content
              </label>
              <p className="text-sm text-gray-600">
                Automatically publish content to {platform.name} when ready
              </p>
            </div>
            <Switch
              checked={formState.autopost_enabled}
              onCheckedChange={(checked) => handleChange('autopost_enabled', checked)}
              disabled={!formState.autogenerate_enabled}
              aria-label="Auto-publish content"
              className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-blue-500 data-[state=checked]:to-indigo-600"
            />
          </div>

          {/* Time Selection - only visible when auto-publish is enabled */}
          {formState.autopost_enabled && (
            <div className="space-y-3 pt-4 border-t border-blue-100">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <label className="text-base font-semibold text-blue-800">
                  Preferred Publishing Time
                </label>
              </div>
              <div className="space-y-2">
                <Input
                  type="time"
                  value={formState.preferred_time}
                  onChange={(e) => handleChange('preferred_time', e.target.value)}
                  className="w-full"
                />
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Your timezone: {timezone ? timezone.replace('_', ' ') : 'Loading...'}
                  {!isLoading && (
                    <span className="ml-2">Current time: {formatCurrentTime()}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Auto-create Behavior Alert - moved to top of component */}

      {/* Media Handling Configuration */}
      <Card className="group relative overflow-hidden border border-purple-200/50 bg-gradient-to-br from-white via-purple-50/20 to-white shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-50/20 via-transparent to-indigo-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-200/20 to-transparent rounded-full -translate-y-10 translate-x-10" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-2 text-xl font-bold bg-gradient-to-r from-purple-800 to-indigo-700 bg-clip-text text-transparent">
            <Cog className="h-5 w-5 text-purple-600" />
            Automated Media Processing
          </CardTitle>
        </CardHeader>
        <CardContent className="relative space-y-6">
          {/* Cost Impact Warning - REMOVED as requested */}

          <p className="text-base text-gray-600">
            Configure how images and videos are handled when creating content for {platform.name}
          </p>

          {/* Image Configuration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="relative text-base font-bold bg-gradient-to-r from-blue-800 to-indigo-700 bg-clip-text text-transparent">Use Images</label>
                <p className="relative text-sm text-gray-600 mt-1">Enable automatic image processing</p>
              </div>
              <Switch
                checked={formState.config.image?.enabled || false}
                onCheckedChange={(checked) => {
                  setFormState(prev => ({
                    ...prev,
                    config: {
                      ...prev.config,
                      image: {
                        enabled: checked,
                        steps: prev.config.image?.steps || ["SEARCH_EXISTING", "AI_GENERATE", "STOCK_SEARCH", "STOP_AND_UPLOAD"]
                      }
                    }
                  }));
                }}
                disabled={!formState.autogenerate_enabled}
                className={cn(
                  "data-[state=checked]:bg-green-500 data-[state=checked]:hover:bg-green-600"
                )}
              />
            </div>

            {formState.config.image?.enabled && (
              <div className="ml-4 pl-4 border-l-2 border-gray-200 space-y-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                  Image Processing Steps (In Order)
                </label>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleImageDragEnd}
                >
                  <SortableContext
                    items={imageStepsOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {imageStepsOrder.map((step) => (
                        <SortableStepCard
                          key={step}
                          id={step}
                          step={step}
                          enabled={formState.config.image?.steps?.includes(step) || false}
                          onToggle={handleImageStepToggle}
                          disabled={!formState.autogenerate_enabled}
                          type="image"
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <p className="text-xs text-muted-foreground mt-2">
                  Drag cards to reorder • Uncheck to disable a step • Steps run in order from top to bottom
                </p>
              </div>
            )}
          </div>

          {/* Video Configuration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="relative text-base font-bold bg-gradient-to-r from-purple-800 to-pink-700 bg-clip-text text-transparent">Use Videos</label>
                <p className="relative text-sm text-gray-600 mt-1">Enable automatic video processing</p>
              </div>
              <Switch
                checked={formState.config.video?.enabled || false}
                onCheckedChange={(checked) => {
                  setFormState(prev => ({
                    ...prev,
                    config: {
                      ...prev.config,
                      video: {
                        enabled: checked,
                        steps: prev.config.video?.steps || ["SEARCH_EXISTING", "STOP_AND_UPLOAD", "AI_GENERATE", "STOCK_SEARCH"]
                      }
                    }
                  }));
                }}
                disabled={!formState.autogenerate_enabled}
                className={cn(
                  "data-[state=checked]:bg-green-500 data-[state=checked]:hover:bg-green-600"
                )}
              />
            </div>

            {formState.config.video?.enabled && (
              <div className="ml-4 pl-4 border-l-2 border-gray-200 space-y-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                  Video Processing Steps (In Order)
                </label>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleVideoDragEnd}
                >
                  <SortableContext
                    items={videoStepsOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {videoStepsOrder.map((step) => (
                        <SortableStepCard
                          key={step}
                          id={step}
                          step={step}
                          enabled={formState.config.video?.steps?.includes(step) || false}
                          onToggle={handleVideoStepToggle}
                          disabled={!formState.autogenerate_enabled}
                          type="video"
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <p className="text-xs text-muted-foreground mt-2">
                  Drag cards to reorder • Uncheck to disable a step • Steps run in order from top to bottom
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="group relative overflow-hidden min-w-[180px] h-12 bg-gradient-to-r from-purple-500 to-pink-600 border-0 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:from-purple-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none font-medium"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          {saving ? (
            <>
              <div className="relative animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
              <span className="relative">Saving...</span>
            </>
          ) : (
            <>
              <Save className="relative h-4 w-4 mr-2" />
              <span className="relative">Save Automation Settings</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PlatformAutomationConfig;